// Shared loader for StoralyticDemo synthetic data.
//
// All demo JSON files in /public/demo_data/ are frozen snapshots from
// Oct/Nov 2025. To keep the demo feeling fresh ("Top day" landing in the
// past week, "Last updated" landing today, "Trend last date" landing
// yesterday, etc.) every date we read is rebased on the fly by a single
// offset:
//
//   shiftDays = today − DEMO_ANCHOR_DATE
//
// The anchor is calibrated so that the latest timeseries date
// (2025-10-31 in the source snapshot, i.e. the "yesterday" of the export)
// lands on today − 1 after shifting. That requires anchor =
// 2025-11-01 — one day after the timeseries max. Per-file dates such as
// `last_updated_date` are pre-set in the source JSON to match this anchor
// (i.e. "2025-11-01"), so they land on today after shifting.
//
// Supported date shapes (each handled in both values AND object keys):
//   YYYY-MM-DD
//   YYYY-MM-DD HH:MM:SS[.ffffff]
//   YYYY-MM-DDTHH:MM:SS[.ffffff][±HH:MM | Z]

import { withNl } from '@/demo/demoLang'

const DEMO_ANCHOR_DATE = '2025-11-01'

const DATE_ONLY_RE     = /^(\d{4})-(\d{2})-(\d{2})$/
const DATE_SPACE_RE    = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?)$/
const DATE_ISO_RE      = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2}:\d{2}(?:\.\d+)?)((?:[+-]\d{2}:\d{2})|Z)?$/

const MS_PER_DAY = 24 * 60 * 60 * 1000

let cachedShiftDays = null

// Whole-week shift, computed from LITERALS only — NO module-scope const reads. This is
// deliberate: in Vite's bundled chunk a component's top-level code (e.g. Guidance's
// `const TODAY = demoDaysAgo(5)`) can run BEFORE this module's `const DEMO_ANCHOR_DATE`
// initialises. Reading a const there throws "Cannot access X before initialization"
// (temporal dead zone) and took the whole app down. A hoisted function using literals
// is callable at any point and sidesteps that entirely.
function wholeWeekShiftDays() {
  const anchor = new Date('2025-11-01T00:00:00Z')   // === DEMO_ANCHOR_DATE
  const now    = new Date()
  const today  = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  const rawDays = Math.round((today - anchor.getTime()) / 86400000)
  // Only ever slide by multiples of 7 so weekday patterns (weekend peaks, "busiest
  // day") stay aligned — a source date keeps its weekday; guarded so the anchor never
  // lands in the future (demo data sits within the past ~week, weekday-true).
  let weekShift = Math.round(rawDays / 7) * 7
  if (weekShift > rawDays) weekShift -= 7
  return weekShift
}

function computeShiftDays() {
  if (cachedShiftDays !== null) return cachedShiftDays
  cachedShiftDays = wholeWeekShiftDays()
  return cachedShiftDays
}

// Demo "today" — the anchor after the whole-week shift (lands within ~a week of the real
// opening date, on the anchor's weekday). EVERY inline demo date should hang off this,
// so nothing is ever a frozen calendar date again. Self-contained (see the note above)
// so it is safe to call from a component's module top level.
export function demoToday() {
  const anchor = new Date('2025-11-01T00:00:00Z')
  anchor.setUTCDate(anchor.getUTCDate() + wholeWeekShiftDays())
  return anchor   // UTC-midnight Date
}

// A demo date `n` days before demo-today (n may be negative for future dates), as a
// UTC-midnight Date. Use this to re-express any hardcoded inline date as an offset.
export function demoDaysAgo(n) {
  const d = demoToday()
  d.setUTCDate(d.getUTCDate() - n)
  return d
}

// "YYYY-MM-DD" for a demo date n days before demo-today (handy for inline literals).
export function demoDateStr(n = 0) {
  return demoDaysAgo(n).toISOString().slice(0, 10)
}

function shiftYMD(y, m, d, shiftDays) {
  const dt = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)))
  dt.setUTCDate(dt.getUTCDate() + shiftDays)
  const yy = dt.getUTCFullYear()
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(dt.getUTCDate()).padStart(2, '0')
  return [yy, mm, dd]
}

// Returns a shifted string, or the original input if no date pattern matches.
function shiftDateString(value, shiftDays) {
  if (typeof value !== 'string' || value.length < 10) return value

  let m = value.match(DATE_ONLY_RE)
  if (m) {
    const [yy, mm, dd] = shiftYMD(m[1], m[2], m[3], shiftDays)
    return `${yy}-${mm}-${dd}`
  }
  m = value.match(DATE_SPACE_RE)
  if (m) {
    const [yy, mm, dd] = shiftYMD(m[1], m[2], m[3], shiftDays)
    return `${yy}-${mm}-${dd} ${m[4]}`
  }
  m = value.match(DATE_ISO_RE)
  if (m) {
    const [yy, mm, dd] = shiftYMD(m[1], m[2], m[3], shiftDays)
    return `${yy}-${mm}-${dd}T${m[4]}${m[5] || ''}`
  }
  return value
}

// Walk the parsed JSON in place, shifting any date strings (values *or*
// object keys). Mutates and returns the same reference.
function rebaseDatesInPlace(node, shiftDays) {
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      const child = node[i]
      if (typeof child === 'string') {
        node[i] = shiftDateString(child, shiftDays)
      } else if (child && typeof child === 'object') {
        rebaseDatesInPlace(child, shiftDays)
      }
    }
    return node
  }
  if (node && typeof node === 'object') {
    // Collect keys first; renaming during iteration is unsafe.
    const keys = Object.keys(node)
    for (const k of keys) {
      const child = node[k]
      if (typeof child === 'string') {
        node[k] = shiftDateString(child, shiftDays)
      } else if (child && typeof child === 'object') {
        rebaseDatesInPlace(child, shiftDays)
      }

      const newKey = shiftDateString(k, shiftDays)
      if (newKey !== k) {
        node[newKey] = node[k]
        delete node[k]
      }
    }
  }
  return node
}

function normalizeUrl(path) {
  if (/^https?:\/\//i.test(path)) return path
  const base = (import.meta.env.BASE_URL || '/')
  if (path.startsWith(base)) return path
  return base.replace(/\/$/, '') + '/' + path.replace(/^\//, '')
}

async function fetchDemoJSON(path, { signal, rebase = true } = {}) {
  const url = normalizeUrl(path)
  const res = await fetch(url, { signal, cache: 'no-store' })
  if (!res.ok) throw new Error(`${url}: ${res.status} ${res.statusText}`)
  const data = await res.json()
  if (!rebase) return data
  const shiftDays = computeShiftDays()
  if (shiftDays === 0) return data
  return rebaseDatesInPlace(data, shiftDays)
}

// Demo files that also exist in Dutch, as <name>.nl.json next to the English one
// (sample sentences). Both are loaded; each text that differs, at any depth, then
// reads in the portal's current language (withNl), so a language switch needs no
// reload. The Dutch file is the same shape with only those sentences changed.
const DUTCH_VARIANTS = new Set(['demo_coach_digest.json', 'demo_service_response.json'])

function mergeDutch(en, nl) {
  if (!en || !nl || typeof en !== 'object' || typeof nl !== 'object') return en
  const nlText = {}
  for (const [k, v] of Object.entries(nl)) {
    if (typeof v === 'string' && typeof en[k] === 'string' && v !== en[k]) nlText[k] = v
    else if (v && typeof v === 'object') mergeDutch(en[k], v)
  }
  return withNl(en, nlText)
}

// Fetches a demo JSON file from /public/demo_data/ and rebases every
// date it contains. Set `rebase: false` to opt out (e.g. for files that
// already use relative dates).
export async function loadDemoJSON(path, opts = {}) {
  const data = await fetchDemoJSON(path, opts)
  if (!DUTCH_VARIANTS.has(String(path).split('/').pop())) return data
  let nl = null
  try {
    nl = await fetchDemoJSON(String(path).replace(/\.json$/, '.nl.json'), opts)
  } catch (_) {
    return data   // no Dutch file: the English stands
  }
  return mergeDutch(data, nl)
}

// Exposed for tests / debugging.
export const __demoDataInternals = {
  DEMO_ANCHOR_DATE,
  computeShiftDays,
  shiftDateString,
  rebaseDatesInPlace,
}
